// @flow
import * as React from "react";
import moment from "moment";
import Media from "react-media";
import { connect } from "react-redux";
import RawLink from "../../Link";
import styled, { css } from "styled-components";
import idx from "idx";
import Gettext from "../../../common/Gettext";
import Spinner from "../../../Spinners/SimpleSpinner";
import TournamentApi from "../../Api";

import { PrizeFund as _PrizeFund } from "../../../Tournament/Info";
import ButtonSwitcher from "../../Account/Card/ButtonSwitcher";
import Sticker from "../../Sticker";
import { ModalConsumer } from "../../../common/ModalProvider";

import { JoinPopup, LeavePopup } from "../";

import RawTimer, {
  getActive,
  getNew,
  getDuration,
  getCompleted,
  mapStateToTime
} from "../../Timer";

import tournamentBgIron from "../../images/default-tournament-bg-iron.jpg";
import tournamentBgSiam from "../../images/default-tournament-bg-th.jpg";

const SharedContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  flex-grow: 0;
  flex-shrink: 0;
  align-self: flex-end;
  width: 100%;
  margin-top: 10px;
  background-color: ${({ theme }) => theme.main};
  background-image: url("${props =>
    props.bg
      ? props.bg
      : process.env.REACT_APP_DEPLOY_TARGET === "siamoption"
        ? tournamentBgSiam
        : tournamentBgIron}");
    background-repeat: no-repeat;
    background-position: center;
  background-size: cover;
  background-position: 50%;

  @media(min-width: 960px) {
    min-height: 100px;
    margin-top: 15px;
  }
`;

const SpinnerContainer = styled(SharedContainer)`
  align-items: center;
  justify-content: center;
  align-content: center;
  min-height: 100px;
`;

const Container = styled(SharedContainer)`
  align-items: flex-start;
  padding: 15px;

  @media (max-width: 959px) {
    padding: 10px 15px;
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.alternativeForeground};
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-content: flex-start;
  justify-content: flex-start;
  width: 100%;

  :not(:first-of-type) {
    margin-top: 15px;
  }
`;

const Info = styled(Row)`
  @media (max-width: 959px) {
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    align-content: flex-start;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-content: center;
  flex-basis: 163px;
  min-width: 163px;

  ${({ theme }) => {
    if (theme.direction === "rtl")
      return css`
        margin-right: auto;
      `;

    return css`
      margin-left: auto;
    `;
  }};
`;

const ButtonActionContainer = styled(ActionContainer)`
  @media (min-width: 640px) {
    ${({ theme }) => {
      if (theme.direction === "rtl")
        return css`
          margin-right: auto;
        `;

      return css`
        margin-left: auto;
      `;
    }};
  }

  @media (max-width: 639px) {
    margin: 0 auto;
  }
`;

const PrizeFund = styled(_PrizeFund)`
  font-size: 14px;
  line-height: 1;
  color: #99959d;

  @media (max-width: 959px) {
    margin-bottom: 10px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  flex: 0;

  @media (max-width: 959px) {
    flex-basis: 27px;
    min-width: 27px;
  }

  ${({ theme }) => {
    if (theme.direction === "rtl")
      return css`
        margin-left: 10px;
      `;

    return css`
      margin-right: 10px;
    `;
  }};
`;

const Timer = styled(RawTimer)`
  font-size: 12px;
  line-height: 1;

  @media (min-width: 480px) {
    ${({ theme }) => {
      if (theme.direction === "rtl")
        return css`
          margin-right: 15px;
          text-align: right;
        `;

      return css`
        margin-left: 15px;
        text-align: left;
      `;
    }};
  }

  @media (max-width: 959px) {
    margin-bottom: 10px;
  }
`;

const Link = styled(RawLink)`
  margin-top: 5px;

  @media (min-width: 960px) {
    margin-top: 0;
  }
`;

type SharedProps = {|
  dispatch: Function,
  onDismiss: () => void,
  time: number
|};

type Props = {
  ...SharedProps,
  showModal: Function,
  api: any
};

const LearnMore = ({ to }: { to: string }) => (
  <Link to={to}>
    <Gettext>Learn more</Gettext>
  </Link>
);

class NextTournament extends React.Component<Props, {}> {
  onTradeClick = async (id: number) => {
    const { api, onDismiss } = this.props;
    const activate = await api.fetchEntity(
      `/api/tournaments/activate?id=${id}`
    );

    if (activate !== null) {
      api.fetchEntity(`/api/tournaments/list?status=1`);
      api.fetchEntity(`/api/tournaments/info?id=${id}`);
      await api.fetchEntity(`/api/tournaments/stats`);
      this.props.dispatch({
        type: "ENGAGE_TOURNAMENT_MODE"
      });
      onDismiss();
    }
  };

  componentDidMount() {
    this.props.api.fetchEntity("/api/tournaments/list?status=1");
  }

  render() {
    const { api, time, onDismiss, dispatch, showModal } = this.props;
    const list = api.readEntity("/api/tournaments/list?status=1");

    const tournament = idx(list, list => list.value.items[0]);

    if (tournament == null) {
      const { status } = list;

      switch (status) {
        case "Resolved":
        case "Rejected":
          return (
            <SpinnerContainer>
              <Gettext>There are no tournaments available.</Gettext>
            </SpinnerContainer>
          );
        default:
          return (
            <SpinnerContainer>
              <Spinner accent height="100px" />
            </SpinnerContainer>
          );
      }
    }

    const bg = idx(tournament, tournament => tournament.images.bar);
    const timerProps = { ...tournament, time };

    const isAndroid = window.androidApp !== undefined;

    return (
      <Container bg={bg}>
        <Row>
          <Title>{tournament.title}</Title>
          <Media
            query="(min-width: 960px)"
            render={() => (
              <ActionContainer>
                <LearnMore to={`/tournaments/${tournament.id}`} />
              </ActionContainer>
            )}
          />
        </Row>
        <Info>
          <PrizeFund {...tournament} IconWrapper={IconContainer} />
          <Timer
            completedTimer={moment.unix(tournament.finish_date).format("LLL")}
            days={getDuration(timerProps, "days")}
            hours={getDuration(timerProps, "hours")}
            minutes={getDuration(timerProps, "minutes")}
            seconds={getDuration(timerProps, "seconds")}
            active={getActive(timerProps)}
            isCompleted={getCompleted(timerProps)}
          />
          <ButtonActionContainer>
            {getCompleted(timerProps) ? null : (
              <ButtonSwitcher
                flat={true}
                {...tournament}
                onActiveTradeClick={
                  isAndroid
                    ? () => window.androidApp.tournamentTrade(tournament.id)
                    : onDismiss
                }
                onJoinClick={() =>
                  showModal(JoinPopup, { ...tournament, dispatch })
                }
                onLeaveClick={() =>
                  showModal(LeavePopup, { ...tournament, dispatch })
                }
                onTradeClick={
                  isAndroid
                    ? () => window.androidApp.tournamentTrade(tournament.id)
                    : () => this.onTradeClick(tournament.id)
                }
              />
            )}
            <Media
              query="(max-width: 959px)"
              render={() => <LearnMore to={`/tournaments/${tournament.id}`} />}
            />
          </ButtonActionContainer>
        </Info>
        <Sticker
          {...tournament}
          active={getActive(timerProps)}
          isNew={getNew(timerProps)}
        />
      </Container>
    );
  }
}

export default connect(mapStateToTime)((props: SharedProps) => (
  <ModalConsumer>
    {({ showModal }) => (
      <TournamentApi.Consumer>
        {api => <NextTournament api={api} showModal={showModal} {...props} />}
      </TournamentApi.Consumer>
    )}
  </ModalConsumer>
));



// WEBPACK FOOTER //
// ./src/components/Tournaments/Popups/Final/NextTournament.js